import { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";

const contactInfo = [
    {
        titleKey: "phone",
        value: "+374 93-11-81-12",
        descriptionKey: "phoneDescription",
        href: "tel:+37493118112",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
                <path
                    d="M7 3H5C3.9 3 3 3.9 3 5C3 13.84 10.16 21 19 21C20.1 21 21 20.1 21 19V17L16 15L14 17C11 16 8 13 7 10L9 8L7 3Z"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        ),
    },
    {
        titleKey: "email",
        value: "info@seaway.am",
        descriptionKey: "emailDescription",
        href: "mailto:info@seaway.am",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
                <rect
                    x="3"
                    y="5"
                    width="18"
                    height="14"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.7"
                />
                <path
                    d="M4 7L12 13L20 7"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        ),
    },
    {
        titleKey: "location",
        valueKey: "address",
        descriptionKey: "locationDescription",
        href: null,
        icon: (
            <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
                <path
                    d="M20 10C20 15 12 21 12 21S4 15 4 10A8 8 0 1 1 20 10Z"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinejoin="round"
                />
                <circle
                    cx="12"
                    cy="10"
                    r="2.5"
                    stroke="currentColor"
                    strokeWidth="1.7"
                />
            </svg>
        ),
    },
];

const ContactContent = () => {
    const { contact } = useI18n();
    const [isServiceOpen, setIsServiceOpen] = useState(false);
    const [activeServiceIndex, setActiveServiceIndex] = useState(0);
    const serviceSelectorRef = useRef(null);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
    });

    const [status, setStatus] = useState("idle");

    const serviceOptions = [
        { value: "sea", label: contact.seaFreight },
        { value: "air", label: contact.airFreight },
        { value: "road", label: contact.groundFreight },
        { value: "other", label: contact.otherInquiry },
    ];
    const selectedServiceIndex = serviceOptions.findIndex(
        (option) => option.value === form.service
    );
    const selectedService = serviceOptions[selectedServiceIndex];

    useEffect(() => {
        const handlePointerDown = (event) => {
            if (
                serviceSelectorRef.current &&
                !serviceSelectorRef.current.contains(event.target)
            ) {
                setIsServiceOpen(false);
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);
        return () => document.removeEventListener("pointerdown", handlePointerDown);
    }, []);

    const selectService = (value) => {
        setForm((previous) => ({ ...previous, service: value }));
        setIsServiceOpen(false);
    };

    const handleServiceKeyDown = (event) => {
        if (event.key === "Escape" && isServiceOpen) {
            event.preventDefault();
            setIsServiceOpen(false);
            return;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            const direction = event.key === "ArrowDown" ? 1 : -1;

            if (!isServiceOpen) {
                setActiveServiceIndex(
                    selectedServiceIndex >= 0
                        ? selectedServiceIndex
                        : direction > 0
                          ? 0
                          : serviceOptions.length - 1
                );
                setIsServiceOpen(true);
                return;
            }

            setActiveServiceIndex((index) =>
                (index + direction + serviceOptions.length) % serviceOptions.length
            );
            return;
        }

        if (event.key === "Home" || event.key === "End") {
            event.preventDefault();
            setActiveServiceIndex(
                event.key === "Home" ? 0 : serviceOptions.length - 1
            );
            setIsServiceOpen(true);
            return;
        }

        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (isServiceOpen) {
                selectService(serviceOptions[activeServiceIndex].value);
            } else {
                setActiveServiceIndex(
                    selectedServiceIndex >= 0 ? selectedServiceIndex : 0
                );
                setIsServiceOpen(true);
            }
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;

        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (status === "pending") return;

        setStatus("pending");

        try {
            const response = await fetch("/api/contact.php", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(form),
            });

            const result = await response.json().catch(() => null);

            if (!response.ok || !result?.success) {
                throw new Error(result?.message || "Unable to send your message.");
            }

            setStatus("success");
            setForm({
                name: "",
                email: "",
                phone: "",
                service: "",
                message: "",
            });
        } catch (error) {
            console.error("Contact form submission failed:", error);
            setStatus("error");
        }
    };

    const inputClass =
        "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-[17px] text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-sky-500 focus:bg-white focus:ring-2 focus:ring-sky-100";

    return (
        <section className="bg-white px-6 py-16 md:px-12 md:py-24">
            <div className="mx-auto max-w-7xl">
                {/* Contact cards */}
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {contactInfo.map((item) => {
                        const content = (
                            <>
                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                                    {item.icon}
                                </div>

                                <h3 className="mt-5 text-lg font-bold text-slate-900">
                                    {contact[item.titleKey]}
                                </h3>

                                <p className="mt-2 break-words text-base font-semibold text-sky-700">
                                    {item.valueKey ? contact[item.valueKey] : item.value}
                                </p>

                                <p className="mt-2 text-sm text-slate-500">
                                    {contact[item.descriptionKey]}
                                </p>
                            </>
                        );

                        return item.href ? (
                            <a
                                key={item.titleKey}
                                href={item.href}
                                className="rounded-2xl border border-slate-200 p-6 transition-colors duration-200 hover:border-sky-300 sm:p-7"
                            >
                                {content}
                            </a>
                        ) : (
                            <div
                                key={item.titleKey}
                                className="rounded-2xl border border-slate-200 p-6 sm:p-7"
                            >
                                {content}
                            </div>
                        );
                    })}
                </div>

                {/* Form and map */}
                <div className="mt-16 grid items-start gap-10 lg:mt-24 lg:grid-cols-2 lg:gap-16">
                    {/* Contact form */}
                    <div>
                        <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-sky-700 sm:text-sm">
                            <span className="h-px w-8 bg-sky-500" />
                            {contact.formEyebrow}
                        </p>

                        <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
                            {contact.formTitle}
                        </h2>

                        <p className="mt-4 leading-7 text-slate-600">
                            {contact.formDescription}
                        </p>

                        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label
                                        htmlFor="contact-name"
                                        className="mb-2 block text-sm font-semibold text-slate-700"
                                    >
                                        {contact.name} *
                                    </label>

                                    <input
                                        id="contact-name"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        className={inputClass}
                                        placeholder={contact.namePlaceholder}
                                        autoComplete="name"
                                        required
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="contact-email"
                                        className="mb-2 block text-sm font-semibold text-slate-700"
                                    >
                                        {contact.emailAddress} *
                                    </label>

                                    <input
                                        id="contact-email"
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        className={inputClass}
                                        placeholder={contact.emailPlaceholder}
                                        autoComplete="email"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label
                                        htmlFor="contact-phone"
                                        className="mb-2 block text-sm font-semibold text-slate-700"
                                    >
                                        {contact.phoneNumber}
                                    </label>

                                    <input
                                        id="contact-phone"
                                        type="tel"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handleChange}
                                        className={inputClass}
                                        placeholder="+374 ..."
                                        autoComplete="tel"
                                    />
                                </div>

                                <div ref={serviceSelectorRef} className="relative">
                                    <label
                                        htmlFor="contact-service"
                                        className="mb-2 block text-sm font-semibold text-slate-700"
                                    >
                                        {contact.service}
                                    </label>

                                    <button
                                        type="button"
                                        id="contact-service"
                                        role="combobox"
                                        aria-haspopup="listbox"
                                        aria-expanded={isServiceOpen}
                                        aria-controls="contact-service-options"
                                        aria-activedescendant={
                                            isServiceOpen
                                                ? `contact-service-option-${activeServiceIndex}`
                                                : undefined
                                        }
                                        onClick={() => {
                                            if (isServiceOpen) {
                                                setIsServiceOpen(false);
                                            } else {
                                                setActiveServiceIndex(
                                                    selectedServiceIndex >= 0
                                                        ? selectedServiceIndex
                                                        : 0
                                                );
                                                setIsServiceOpen(true);
                                            }
                                        }}
                                        onKeyDown={handleServiceKeyDown}
                                        onBlur={(event) => {
                                            if (
                                                !serviceSelectorRef.current?.contains(
                                                    event.relatedTarget
                                                )
                                            ) {
                                                setIsServiceOpen(false);
                                            }
                                        }}
                                        className={`${inputClass} flex items-center justify-between gap-3 text-left ${
                                            isServiceOpen
                                                ? "border-sky-500 bg-white ring-2 ring-sky-100"
                                                : ""
                                        }`}
                                    >
                                        <span
                                            className={
                                                selectedService
                                                    ? "text-slate-900"
                                                    : "text-slate-400"
                                            }
                                        >
                                            {selectedService?.label || contact.selectService}
                                        </span>
                                        <svg
                                            aria-hidden="true"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 ${
                                                isServiceOpen ? "rotate-180 text-sky-600" : ""
                                            }`}
                                        >
                                            <path
                                                d="m5 7.5 5 5 5-5"
                                                stroke="currentColor"
                                                strokeWidth="1.7"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </button>

                                    {isServiceOpen && (
                                        <ul
                                            id="contact-service-options"
                                            role="listbox"
                                            aria-label={contact.service}
                                            className="absolute z-20 mt-2 max-h-64 w-full overflow-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-xl shadow-slate-900/10"
                                        >
                                            {serviceOptions.map((option, index) => {
                                                const isSelected =
                                                    option.value === form.service;
                                                const isActive =
                                                    index === activeServiceIndex;

                                                return (
                                                    <li
                                                        key={option.value}
                                                        id={`contact-service-option-${index}`}
                                                        role="option"
                                                        aria-selected={isSelected}
                                                        onMouseDown={(event) =>
                                                            event.preventDefault()
                                                        }
                                                        onMouseEnter={() =>
                                                            setActiveServiceIndex(index)
                                                        }
                                                        onClick={() =>
                                                            selectService(option.value)
                                                        }
                                                        className={`flex cursor-pointer items-center justify-between rounded-xl px-4 py-3 text-[16px] transition-colors ${
                                                            isActive
                                                                ? "bg-sky-50 text-sky-800"
                                                                : "text-slate-700 hover:bg-slate-50"
                                                        }`}
                                                    >
                                                        <span className="flex items-center gap-3">
                                                            <span
                                                                aria-hidden="true"
                                                                className={`h-2 w-2 rounded-full ${
                                                                    isSelected
                                                                        ? "bg-sky-500"
                                                                        : "bg-slate-300"
                                                                }`}
                                                            />
                                                            {option.label}
                                                        </span>
                                                        {isSelected && (
                                                            <svg
                                                                aria-hidden="true"
                                                                viewBox="0 0 20 20"
                                                                fill="none"
                                                                className="h-5 w-5 text-sky-600"
                                                            >
                                                                <path
                                                                    d="m4 10 4 4 8-8"
                                                                    stroke="currentColor"
                                                                    strokeWidth="1.8"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor="contact-message"
                                    className="mb-2 block text-sm font-semibold text-slate-700"
                                >
                                    {contact.message} *
                                </label>

                                <textarea
                                    id="contact-message"
                                    name="message"
                                    value={form.message}
                                    onChange={handleChange}
                                    className={`${inputClass} min-h-36 resize-y`}
                                    placeholder={contact.messagePlaceholder}
                                    required
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={status === "pending"}
                                className="inline-flex min-h-12 items-center justify-center gap-3 rounded-xl bg-sky-600 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-sky-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {contact.sendMessage}

                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M5 12h14m-6-6 6 6-6 6"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>

                            {status === "pending" && (
                                <p role="status" aria-live="polite" className="text-sm text-amber-700">
                                    {contact.formPending}
                                </p>
                            )}

                            {status === "success" && (
                                <p role="status" aria-live="polite" className="text-sm font-medium text-emerald-700">
                                    Your message has been sent successfully. We will contact you soon.
                                </p>
                            )}

                            {status === "error" && (
                                <p role="alert" className="text-sm font-medium text-red-600">
                                    Something went wrong. Please try again or contact us by email.
                                </p>
                            )}
                        </form>
                    </div>

                    {/* Office location map */}
                    <div>
                        <div className="mb-5">
                            <h2 className="text-2xl font-extrabold text-slate-900">
                                {contact.mapTitle}
                            </h2>

                            <p className="mt-3 leading-7 text-slate-600">
                                {contact.mapDescription}
                            </p>
                        </div>

                        <div className="min-h-[350px] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 sm:min-h-[450px]">
                            <iframe
                                title={`${contact.mapTitle}: ${contact.address}`}
                                src={`https://www.google.com/maps?q=${encodeURIComponent(contact.address)}&output=embed`}
                                className="h-[350px] w-full sm:h-[450px]"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactContent;